import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  Divider,
  Link,
  Chip
} from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon,
  Payment as PaymentIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  SwapHoriz as SwapIcon,
  Download as DownloadIcon,
  VerifiedUser as VerifiedUserIcon,
  PhoneAndroid as PhoneIcon
} from '@mui/icons-material';
import { PricingTier, PaymentStatus } from '../types';
import { 
  isMetaMaskInstalled, 
  connectToWallet,
  isBaseMainnet, 
  switchToBaseMainnet, 
  processPayment,
  isWalletWhitelisted,
  isWalletProviderAvailable,
  getWalletProviderName
} from '../services/paymentService';
import { PAYMENT_RECEIVER_ADDRESS } from '../config/walletConfig';

interface PaymentModalProps {
  open: boolean;
  onClose: () => void;
  onPaymentComplete: () => void;
  pricingTier: PricingTier;
  nftCount: number;
  walletAddress?: string | null;
  setWalletAddress?: React.Dispatch<React.SetStateAction<string | null>>;
  onDownload?: (forceDownload?: boolean) => void;
}

const PaymentModal: React.FC<PaymentModalProps> = ({
  open,
  onClose,
  onPaymentComplete,
  pricingTier,
  nftCount,
  walletAddress = null,
  setWalletAddress,
  onDownload
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [localWalletAddress, setLocalWalletAddress] = useState<string | null>(walletAddress);
  const [isCorrectNetwork, setIsCorrectNetwork] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>(PaymentStatus.PENDING);
  const [error, setError] = useState<string | null>(null);
  const [isWhitelisted, setIsWhitelisted] = useState(false);
  const [transactionHash, setTransactionHash] = useState<string | null>(null);
  const [hasDownloaded, setHasDownloaded] = useState(false);

  const steps = ['Connect Wallet', 'Switch to Base', 'Make Payment', 'Confirmation'];

  useEffect(() => {
    // Reset state when modal opens
    if (open) {
      // Récupérer l'étape active et le statut de paiement du localStorage
      const savedStep = localStorage.getItem('paymentModalStep');
      const savedStatus = localStorage.getItem('paymentStatus');
      const savedHash = localStorage.getItem('transactionHash');
      
      if (savedStep === '3' && savedStatus === PaymentStatus.COMPLETED.toString()) {
        // Si nous avons un paiement complété sauvegardé, restaurer l'état
        setActiveStep(3);
        setPaymentStatus(PaymentStatus.COMPLETED);
        if (savedHash) {
          setTransactionHash(savedHash);
        }
      } else {
        // Sinon, réinitialiser l'état du modal mais en tenant compte du portefeuille déjà connecté
        if (walletAddress) {
          // Si le portefeuille est déjà connecté, passer directement à l'étape de vérification du réseau
          setLocalWalletAddress(walletAddress);
          
          // Vérifier si le portefeuille est whitelisté
          const whitelisted = isWalletWhitelisted(walletAddress);
          setIsWhitelisted(whitelisted);
          
          if (whitelisted) {
            // Si le portefeuille est whitelisté, compléter automatiquement le paiement
            console.log("PaymentModal - Portefeuille whitelisté, complétion automatique du paiement");
            setTimeout(() => {
              onPaymentComplete();
              onClose();
            }, 500);
          } else {
            // Sinon, vérifier si nous sommes déjà sur le bon réseau
            isBaseMainnet().then(onCorrectNetwork => {
              if (onCorrectNetwork) {
                // Si déjà sur le bon réseau, passer directement à l'étape de paiement
                setIsCorrectNetwork(true);
                setActiveStep(2);
              } else {
                // Sinon, aller à l'étape de changement de réseau
                setActiveStep(1);
              }
            });
          }
        } else {
          // Si le portefeuille n'est pas connecté, commencer à l'étape 0
          setActiveStep(0);
        }
        
        setPaymentStatus(PaymentStatus.PENDING);
      }
      
      setError(null);
      setHasDownloaded(false);
    }
  }, [open, walletAddress, onPaymentComplete, onClose, nftCount, pricingTier]);

  // Effet pour gérer la connexion automatique du portefeuille lorsque le modal s'ouvre
  useEffect(() => {
    if (open && activeStep === 0 && !localWalletAddress) {
      // Tenter de connecter automatiquement le portefeuille si nous sommes à l'étape 0 et qu'aucun portefeuille n'est connecté
      handleConnectWallet();
    }
  }, [open, activeStep, localWalletAddress]);

  const handleConnectWallet = async () => {
    try {
      setError(null);
      
      // Si nous sommes sur mobile avec un navigateur standard (pas dans une app wallet)
      if (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent) && 
          !window.ethereum && 
          !window.coinbaseWalletExtension && 
          !window.navigator.userAgent.match(/Coinbase\/[0-9]/) && 
          !window.navigator.userAgent.match(/MetaMask\/[0-9]/)) {
        // Afficher l'erreur pour que les boutons d'ouverture des wallets s'affichent
        setError("To use a wallet on mobile, please open this page in the MetaMask or Coinbase Wallet app.");
        return;
      }
      
      if (!isWalletProviderAvailable()) {
        setError("No wallet detected. Please install MetaMask or Coinbase Wallet to continue.");
        return;
      }
      
      console.log("Tentative de connexion au portefeuille...");
      const address = await connectToWallet();
      console.log("PaymentModal - handleConnectWallet - adresse connectée:", address);
      
      if (address) {
        setLocalWalletAddress(address);
        if (setWalletAddress) {
          setWalletAddress(address);
        }
        
        // Vérifier si le portefeuille est whitelisté
        const whitelisted = isWalletWhitelisted(address);
        console.log("PaymentModal - handleConnectWallet - isWhitelisted:", whitelisted);
        setIsWhitelisted(whitelisted);
        
        if (whitelisted) {
          console.log("PaymentModal - handleConnectWallet - Portefeuille whitelisté, fermeture du modal et complétion du paiement");
          // Si le portefeuille est whitelisté, fermer le modal et compléter le paiement
          setTimeout(() => {
            onPaymentComplete();
            onClose();
          }, 1000);
          return;
        }
        
        // Vérifier automatiquement si le réseau est correct et passer à l'étape appropriée
        const onBaseMainnet = await isBaseMainnet();
        if (onBaseMainnet) {
          // Si déjà sur Base Mainnet, passer directement à l'étape de paiement
          setIsCorrectNetwork(true);
          setActiveStep(2);
        } else {
          // Sinon, passer à l'étape de changement de réseau
          setActiveStep(1);
        }
      } else {
        setError("Failed to connect to wallet. Please try again.");
      }
    } catch (err) {
      console.error("Wallet connection error:", err);
      setError(err instanceof Error ? err.message : "Failed to connect to wallet");
    }
  };

  const handleSwitchNetwork = async () => {
    try {
      setError(null);
      
      const switched = await switchToBaseMainnet();
      if (switched) {
        setIsCorrectNetwork(true);
        // Passer automatiquement à l'étape de paiement
        setActiveStep(2);
      } else {
        setError("Failed to switch to Base network. Please switch manually in your wallet.");
      }
    } catch (err) {
      console.error("Network switch error:", err);
      setError(err instanceof Error ? err.message : "Failed to switch network");
    }
  };

  const handleMakePayment = async () => {
    try {
      setError(null);
      
      // Vérifier si le réseau est Base Mainnet
      const onBaseMainnet = await isBaseMainnet();
      if (!onBaseMainnet) {
        setError("You need to be on the Base network to make a payment. Please switch networks and try again.");
        setActiveStep(1); // Revenir à l'étape de changement de réseau
        return;
      }
      
      setPaymentStatus(PaymentStatus.PROCESSING);
      
      const txHash = await processPayment(
        pricingTier.price,
        PAYMENT_RECEIVER_ADDRESS, // Utiliser l'adresse de paiement du fichier de configuration
        (status) => {
          setPaymentStatus(status);
          
          if (status === PaymentStatus.COMPLETED) {
            // Passer à l'étape de confirmation et s'assurer qu'elle reste active
            setActiveStep(3);
            
            // Stocker cette valeur dans le localStorage pour persister entre les rechargements
            localStorage.setItem('paymentModalStep', '3');
            localStorage.setItem('paymentStatus', PaymentStatus.COMPLETED.toString());
          }
        }
      );
      
      // Stocker le hash de transaction
      if (txHash) {
        console.log("Payment successful, storing transaction hash:", txHash);
        setTransactionHash(txHash);
        localStorage.setItem('transactionHash', txHash);
      }
    } catch (err) {
      console.error("Payment error:", err);
      setError(err instanceof Error ? err.message : "An error occurred during payment");
      setPaymentStatus(PaymentStatus.FAILED);
    }
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <WalletIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Connect Your Wallet
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Connect your {getWalletProviderName()} to proceed with the payment.
            </Typography>
            
            {isWalletProviderAvailable() && !error && !/Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handleConnectWallet}
                startIcon={<WalletIcon />}
              >
                Connect {getWalletProviderName()}
              </Button>
            ) : (
              <>
                <Alert severity="info" sx={{ mb: 2, textAlign: 'left' }}>
                  <Typography variant="body2" gutterBottom>
                    {error || "No wallet detected. You need a wallet like MetaMask or Coinbase Wallet to continue."}
                  </Typography>
                </Alert>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {/Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ? (
                    <>
                      <Typography variant="body2" sx={{ mb: 2, fontWeight: 'bold', textAlign: 'center' }}>
                        Choose your wallet app:
                      </Typography>
                      
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<PhoneIcon />}
                        onClick={() => {
                          // Format correct pour le deep link MetaMask
                          // Exemple: https://metamask.app.link/dapp/example.com/path
                          const url = window.location.href;
                          const urlWithoutProtocol = url.replace(/^https?:\/\//, '');
                          window.location.href = `https://metamask.app.link/dapp/${urlWithoutProtocol}`;
                        }}
                        sx={{ mb: 1, py: 1.5 }}
                        fullWidth
                      >
                        Open in MetaMask
                      </Button>
                      
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => {
                          // Format correct pour le deep link Coinbase Wallet
                          const url = window.location.href;
                          window.location.href = `https://go.cb-w.com/dapp?cb_url=${encodeURIComponent(url)}`;
                        }}
                        sx={{ py: 1.5 }}
                        fullWidth
                      >
                        Open in Coinbase Wallet
                      </Button>
                      
                      <Typography variant="caption" sx={{ mt: 2, textAlign: 'center', color: 'text.secondary' }}>
                        If you already have the app installed, it will open automatically.
                        Otherwise, you will be redirected to the App Store or Google Play.
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => window.open('https://metamask.io/download/', '_blank')}
                        sx={{ mb: 1 }}
                      >
                        Install MetaMask
                      </Button>
                      
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => window.open('https://www.coinbase.com/wallet/downloads', '_blank')}
                      >
                        Install Coinbase Wallet
                      </Button>
                    </>
                  )}
                </Box>
              </>
            )}
          </Box>
        );
      case 1:
        return (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h6" gutterBottom>
              Switch to Base Network
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Your wallet is currently connected to a different network. Please switch to Base to continue.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSwitchNetwork}
            >
              Switch Network
            </Button>
          </Box>
        );
      case 2:
        return (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <PaymentIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Complete Payment
            </Typography>
            <Typography variant="body2" paragraph>
              You are about to pay <strong>{pricingTier.price} ETH</strong> for {nftCount} NFTs.
            </Typography>
            
            {paymentStatus === PaymentStatus.PENDING && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleMakePayment}
                startIcon={<PaymentIcon />}
              >
                Pay {pricingTier.price} ETH
              </Button>
            )}
            
            {paymentStatus === PaymentStatus.PROCESSING && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <CircularProgress size={40} sx={{ mb: 2 }} />
                <Typography>Processing payment...</Typography>
              </Box>
            )}
            
            {paymentStatus === PaymentStatus.FAILED && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <ErrorIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
                <Typography variant="h6" color="error.main">Payment Failed</Typography>
                <Typography variant="body2">
                  Please try again or contact support.
                </Typography>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleMakePayment}
                  sx={{ mt: 2 }}
                >
                  Retry Payment
                </Button>
              </Box>
            )}
          </Box>
        );
      case 3:
        return (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" color="success.main">Payment Successful!</Typography>
            <Typography variant="body2" paragraph>
              Your collection is now ready for download.
            </Typography>
            
            {/* Afficher les détails de la transaction si disponibles */}
            {transactionHash ? (
              <Box sx={{ mt: 2, mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Transaction Details
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-all', mb: 1 }}>
                  Transaction Hash: {transactionHash}
                </Typography>
                <Button 
                  variant="outlined" 
                  size="small"
                  href={`https://basescan.org/tx/${transactionHash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ mt: 1 }}
                >
                  View on BaseScan
                </Button>
              </Box>
            ) : (
              <Box sx={{ mt: 2, mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Transaction details not available.
                </Typography>
              </Box>
            )}
            
            {/* Bouton de téléchargement */}
            <Button
              variant="contained"
              color="primary"
              startIcon={<DownloadIcon />}
              onClick={() => {
                // Sauvegarder le hash de transaction avant le téléchargement
                if (transactionHash) {
                  console.log("Saving transaction hash before download:", transactionHash);
                  localStorage.setItem('transactionHash', transactionHash);
                }
                
                if (onDownload) {
                  onDownload(true); // Forcer le téléchargement
                  setHasDownloaded(true);
                  // Assurer que nous restons sur l'étape de confirmation
                  setActiveStep(3);
                } else {
                  onPaymentComplete(); // Fallback au comportement précédent
                  setHasDownloaded(true);
                  // Assurer que nous restons sur l'étape de confirmation
                  setActiveStep(3);
                }
                // Ne pas fermer le modal automatiquement
              }}
              sx={{ mt: 2 }}
              disabled={hasDownloaded}
            >
              {hasDownloaded ? 'Download Started' : 'Download Now'}
            </Button>
            
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              {hasDownloaded 
                ? 'You can now close this window.' 
                : 'Click the button above to download your collection.'}
            </Typography>
          </Box>
        );
      default:
        return 'Unknown step';
    }
  };

  // Stocker le hash de transaction dans le localStorage
  useEffect(() => {
    if (transactionHash) {
      console.log("Storing transaction hash in localStorage:", transactionHash);
      localStorage.setItem('transactionHash', transactionHash);
    }
  }, [transactionHash]);

  // Récupérer le hash de transaction du localStorage
  useEffect(() => {
    const savedHash = localStorage.getItem('transactionHash');
    console.log("Retrieved transaction hash from localStorage:", savedHash);
    if (savedHash && !transactionHash) {
      console.log("Setting transaction hash from localStorage");
      setTransactionHash(savedHash);
    }
  }, [transactionHash]);

  // Fonction pour nettoyer le localStorage
  const cleanupLocalStorage = () => {
    localStorage.removeItem('paymentModalStep');
    localStorage.removeItem('paymentStatus');
    localStorage.removeItem('transactionHash');
  };

  // Fonction modifiée pour fermer le modal et nettoyer le localStorage
  const handleClose = () => {
    cleanupLocalStorage();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={undefined}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
    >
      <DialogTitle>
        {activeStep === 3 ? (
          <Typography variant="h5">Payment Successful</Typography>
        ) : (
          <Typography variant="h5">Complete Your Purchase</Typography>
        )}
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {isWhitelisted && (
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="body1" fontWeight="bold">
              Your wallet is whitelisted!
            </Typography>
            <Typography variant="body2">
              You can download your collection for free. No payment required.
            </Typography>
          </Alert>
        )}
        
        {!isWhitelisted && (
          <>
            <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
              <Typography variant="subtitle1" gutterBottom>
                Order Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <Typography variant="body2">
                    {pricingTier.label} Plan ({nftCount} NFTs)
                  </Typography>
                </Grid>
                <Grid item xs={4} sx={{ textAlign: 'right' }}>
                  <Typography variant="body2" fontWeight="bold">
                    {pricingTier.price} ETH
                  </Typography>
                </Grid>
              </Grid>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <Typography variant="subtitle2">
                    Total
                  </Typography>
                </Grid>
                <Grid item xs={4} sx={{ textAlign: 'right' }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {pricingTier.price} ETH <Typography component="span" variant="caption" color="text.secondary">+ gas fees</Typography>
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
            
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            
            {getStepContent(activeStep)}
          </>
        )}
        
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            By proceeding with the payment, you agree to our <Link href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</Link>.
          </Typography>
        </Box>
        
        {/* Avertissement concernant la fermeture du modal */}
        {(paymentStatus === PaymentStatus.COMPLETED || isWhitelisted) && (
          <Alert severity="warning" sx={{ mt: 3 }}>
            <Typography variant="body2" fontWeight="bold">
              Warning: If you close this window without downloading your collection, you may lose access to it.
            </Typography>
            <Typography variant="body2">
              Please make sure to download your collection before closing this window.
            </Typography>
          </Alert>
        )}
      </DialogContent>
      
      <DialogActions>
        {/* Bouton Cancel/Close - visible sauf pendant le traitement du paiement */}
        {paymentStatus !== PaymentStatus.PROCESSING && (
          <Button 
            onClick={handleClose}
            color="inherit"
            variant={paymentStatus === PaymentStatus.COMPLETED ? "outlined" : "text"}
            sx={{ 
              minWidth: 100,
              ...(paymentStatus === PaymentStatus.COMPLETED && { color: 'primary.main' })
            }}
          >
            {paymentStatus === PaymentStatus.COMPLETED ? 'Close' : 'Cancel'}
          </Button>
        )}
        
        {isWhitelisted && (
          <>
            <Button 
              variant="contained" 
              color="success" 
              startIcon={<DownloadIcon />}
              onClick={() => {
                if (onDownload) {
                  onDownload(true); // Forcer le téléchargement
                  setHasDownloaded(true);
                  // Assurer que nous restons sur l'étape actuelle
                  setActiveStep(activeStep);
                } else {
                  onPaymentComplete(); // Fallback au comportement précédent
                  setHasDownloaded(true);
                  // Assurer que nous restons sur l'étape actuelle
                  setActiveStep(activeStep);
                }
                // Ne pas fermer le modal automatiquement
              }}
              disabled={hasDownloaded}
            >
              {hasDownloaded ? 'Download Started' : 'Download Collection'}
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mb: -1, mx: 2 }}>
              {hasDownloaded 
                ? 'You can now close this window.' 
                : 'Click the button above to download your collection.'}
            </Typography>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PaymentModal; 